import { Fragment, type JSX, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import Application from '@common/containers/Application';
import ErrorBoundary from '@common/containers/ErrorBoundary';
import NewFeatureContainer from '@common/containers/NewFeatureContainer';
import PrivateRouteContainer from '@common/containers/PrivateRouteContainer';
import RestrictedRouteContainer from '@common/containers/RestrictedRouteContainer';
import Imprint from '@common/routes/Imprint';
import { useAppDispatch } from '@common/store';
import { logout } from '@common/store/actions/async';
import { mobileAppInitialPath } from '@common/utils/device/mobileAppConstants';

const Account = lazy(() => import('@sw/routes/Account'));
const BetaFeatures = lazy(() => import('@sw/routes/BetaFeatures'));
const CreateMassUpdate = lazy(() => import('@sw/routes/CreateMassUpdate'));
const DeviceBackups = lazy(() => import('@sw/routes/DeviceOverview/Backups'));
const DeviceDiagram = lazy(() => import('@sw/routes/DeviceOverview/Diagram'));
const DeviceGeneral = lazy(() => import('@sw/routes/DeviceOverview/General'));
const DeviceOverview = lazy(() => import('@sw/routes/DeviceOverview'));
const DeviceRegisters = lazy(() => import('@sw/routes/DeviceOverview/Registers'));
const DevicePrograms = lazy(() => import('@sw/routes/DeviceOverview/Programs'));
const DeviceStatistics = lazy(() => import('@sw/routes/DeviceOverview/Statistics'));
const DevicesList = lazy(() => import('@sw/routes/DevicesList'));
const DesignHelpers = lazy(() => import('@sw/routes/DesignHelpers'));
const FirmwareList = lazy(() => import('@sw/routes/Firmware'));
const Home = lazy(() => import('@sw/routes/Home'));
const Login = lazy(() => import('@common/routes/Login'));
const MassUpdate = lazy(() => import('@sw/routes/MassUpdate'));
const MassUpdateDetails = lazy(() => import('@sw/routes/MassUpdate/MassUpdateDetails'));
const PasswordCreate = lazy(() => import('@common/routes/PasswordCreate'));
const PasswordReset = lazy(() => import('@common/routes/PasswordReset'));
const PortalKeys = lazy(() => import('@sw/routes/PortalKeys'));
const ResetCredentials = lazy(() => import('@sw/routes/ResetCredentials'));
const ResolveAccessRequest = lazy(() => import('@sw/routes/ResolveAccessRequest'));
const Terms = lazy(() => import('@common/routes/Terms'));
const Unregister = lazy(() => import('@sw/routes/Unregister'));
const VerifyEmail = lazy(() => import('@sw/routes/Account/VerifyEmail'));
const VerifyLegacyEmail = lazy(() => import('@sw/routes/ResetCredentials/VerifyLegacyEmail'));
const MobileContainer = lazy(() => import('@sw/routes/MobileContainer'));
const PrivacyPolicy = lazy(() => import('@common/routes/PrivacyPolicy'));
const DeviceRegistration = lazy(() => import('@sw/routes/Registration'));

function NoAccess(): JSX.Element {
  const dispatch = useAppDispatch();
  dispatch(logout());
  return <span />;
}

const ErrorBoundaryWrapper = DEBUG ? Fragment : ErrorBoundary;

function ApplicationRouter() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_PATH}>
      <Application>
        <ErrorBoundaryWrapper>
          <NewFeatureContainer>
            <Routes>
              <Route path="registration">
                <Route path="isg" element={<DeviceRegistration type="isg" />} />
                <Route path="device" element={<DeviceRegistration type="device" />} />
                <Route index element={<Navigate to="/" replace />} />
              </Route>
              <Route path={mobileAppInitialPath} element={<MobileContainer />} />
              <Route path="imprint" element={<Imprint />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="login" element={<Login />} />
              <Route path="password-reset" element={<PasswordReset />} />
              <Route path="password-create/:verificationToken" element={<PasswordCreate />} />
              <Route path="resolve-access-request/:id/:key" element={<ResolveAccessRequest />} />
              <Route path="verify-email/:token" element={<VerifyEmail />} />
              <Route path="verify-legacy-email/:token" element={<VerifyLegacyEmail />} />
              <Route path="reset-credentials" element={<ResetCredentials />} />
              <Route
                path="terms"
                element={(
                  <PrivateRouteContainer
                    requiredRight="canAccessServiceWelt"
                    onViolation="/"
                    isTermsRoute
                  >
                    <Terms />
                  </PrivateRouteContainer>
                )}
              />
              <Route
                element={(
                  <PrivateRouteContainer requiredRight="canAccessServiceWelt" onViolation="/" />
                )}
              >
                <Route path="unregister/:installationId/:token" element={<Unregister />} />
                <Route path="device/:installationId" element={<DeviceOverview />}>
                  <Route path="general/:groupId?" element={<DeviceGeneral />} />
                  <Route path="parameters/:groupId?" element={<DeviceRegisters />} />
                  <Route path="diagram/:tab?" element={<DeviceDiagram />} />
                  <Route path="programs" element={<DevicePrograms />} />
                  <Route
                    element={<RestrictedRouteContainer requiredRight="isStaff" onViolation="/" />}
                  >
                    <Route path="backups" element={<DeviceBackups />} />
                  </Route>
                  <Route
                    element={(
                      <RestrictedRouteContainer
                        requiredRight="canSeeStatisticsTab"
                        onViolation="/"
                      />
                    )}
                  >
                    <Route path="statistics" element={<DeviceStatistics />} />
                  </Route>
                  <Route index element={<Navigate to="general" replace />} />
                  <Route path="*" element={<Navigate to="../general" replace />} />
                </Route>
              </Route>
              <Route
                element={(
                  <PrivateRouteContainer
                    requiredRight="canUseMassUpdatesTab"
                    onViolation="/"
                  />
                )}
              >
                <Route path="mass-update/details/:massUpdateId" element={<MassUpdateDetails />} />
              </Route>
              <Route
                path="/*"
                element={(
                  <PrivateRouteContainer
                    requiredRight="canAccessServiceWelt"
                    onViolation={<NoAccess />}
                  />
                )}
              >
                <Route element={<Home />}>
                  <Route path="account" element={<Account />} />
                  <Route path="beta-features" element={<BetaFeatures />} />
                  <Route
                    element={(
                      <RestrictedRouteContainer
                        requiredRight="canUsePortalKeysTab"
                        onViolation="/"
                      />
                    )}
                  >
                    <Route path="portal-keys" element={<PortalKeys />} />
                  </Route>
                  <Route
                    element={(
                      <RestrictedRouteContainer
                        requiredRight="canUseFirmwaresTab"
                        onViolation="/"
                      />
                    )}
                  >
                    <Route path="firmwares" element={<FirmwareList />} />
                  </Route>
                  <Route
                    element={(
                      <RestrictedRouteContainer
                        requiredRight="canUseMassUpdatesTab"
                        onViolation="/"
                      />
                    )}
                  >
                    <Route path="mass-update">
                      <Route path="overview" element={<MassUpdate />} />
                      <Route path="create" element={<CreateMassUpdate />} />
                      <Route path="*" element={<Navigate to="../overview" replace />} />
                    </Route>
                  </Route>
                  <Route
                    element={(
                      <RestrictedRouteContainer
                        requiredRight="canUseDesignHelpers"
                        onViolation="/"
                      />
                    )}
                  >
                    <Route path="design" element={<DesignHelpers />} />
                  </Route>
                  <Route path="devices/:view?" element={<DevicesList />} />
                </Route>
                <Route path="*" element={<Navigate to="/devices" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/devices" replace />} />
            </Routes>
          </NewFeatureContainer>
        </ErrorBoundaryWrapper>
      </Application>
    </BrowserRouter>
  );
}

export default ApplicationRouter;
